import { Spacing } from '@persona-id/design-tokens';
import { FocusEvent, useCallback, useEffect, useRef } from 'react';
import Markdown from 'shared/components/Markdown';
import styled, { css } from 'styled-components';

import Stack from 'design-system/components/Stack';
import { isDisabled, isFocused, isNotDisabled } from 'design-system/lib/pseudoClass';
import theme from 'design-system/theme';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCheckboxChecked from 'images/icons/inquiry/checkbox-checked.svg';
import IconCheckboxIndeterminate from 'images/icons/inquiry/checkbox-indeterminate.svg';
import IconCheckboxUnchecked from 'images/icons/inquiry/checkbox-unchecked.svg';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, fontWeightCss, sizeCss } from 'lib/inquiryStyles';
import { InputCheckboxComponent } from 'lib/interfaces/ui-components';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

const CheckboxContainer = styled.div<{ styles: InputCheckboxComponent['styles'] }>(
  ({ styles, 'aria-readonly': ariaReadonly }) => ({
    cursor: ariaReadonly ? 'initial' : 'pointer',
    overflowWrap: 'break-word',
    display: 'flex',
    flexWrap: 'nowrap',
    // Alignment: How the checkbox is aligned on Y-axis if there are many lines of text
    // app/models/inquiry/ui_component/styles/input_checkbox_base.rb:8
    alignItems: styles?.['alignment'].base.base,
  }),
);

const CheckboxLabel = styled(Markdown)<{
  styles: InputCheckboxComponent['styles'];
  disabled: boolean;
}>`
  padding: 0 ${Spacing[2]};
  /**
   * Elsewhere, Markdown adds significant spacing between paragraphs / newlines.
   * Newlines in checkboxes shouldn't be treated as independent paragraphs.
   **/
  p + p {
    margin: 0 !important;
  }
  ${({ styles, disabled }) => ({
    // Text styles
    /* TODO [strict-migration] The typing is broken here; please fix! */
    fontFamily: fontFamilyCss(styles?.['font-family'].base.base) as any,
    fontSize: sizeCss(styles?.['font-size'].base.base) as any,
    fontWeight: fontWeightCss(styles?.['font-weight'].base.base) as any,
    letterSpacing: sizeCss(styles?.['letter-spacing'].base.base) as any,
    lineHeight: sizeCss(styles?.['line-height'].base.base) as any,
    color: disabled
      ? theme.color.text.disabled
      : (colorCss(styles?.['text-color'].base.base) as any),

    a: {
      color: colorCss(styles?.['text-color-highlight'].base.base) as any,
      outlineColor: colorCss(styles?.['text-color-highlight'].base.base) as any,
    },
  })}
`;

const CheckboxDescription = styled(Markdown)<{
  styles: InputCheckboxComponent['styles'];
  disabled: boolean;
}>`
  padding: 0 ${Spacing[2]};
  /**
   * Elsewhere, Markdown adds significant spacing between paragraphs / newlines.
   * Newlines in checkboxes shouldn't be treated as independent paragraphs.
   **/
  p + p {
    margin: 0 !important;
  }
  ${({ styles, disabled }) => {
    return {
      // Text styles
      /* TODO [strict-migration] The typing is broken here; please fix! */
      fontFamily: fontFamilyCss(styles?.['font-family']['description'].base) as any,
      fontSize: sizeCss(styles?.['font-size']['description'].base) as any,
      fontWeight: fontWeightCss(styles?.['font-weight']['description'].base) as any,
      letterSpacing: sizeCss(styles?.['letter-spacing']['description'].base) as any,
      lineHeight: sizeCss(styles?.['line-height']['description'].base) as any,
      color: disabled
        ? (theme.color.text.disabled as any)
        : (colorCss(styles?.['text-color']['description'].base) as any),

      a: {
        color: colorCss(styles?.['text-color-highlight'].base.base) as any,
        outlineColor: colorCss(styles?.['text-color-highlight'].base.base) as any,
      },
    };
  }}
`;

const HiddenInput = styled.input`
  height: 1px;
  width: 1px;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  position: absolute;
`;

const IconContainer = styled.div<{
  focused: boolean;
  disabled: boolean;
  styles: InputCheckboxComponent['styles'];
}>`
  display: flex;
  flex: none;
  align-items: center;
  transition: box-shadow ${TransitionDuration.Fast};

  ${({ styles }) => css`
    svg {
      .checkbox__border--empty {
        fill: ${colorCss(styles?.['fill-color']['checkbox-unchecked'].base)};
        stroke: ${colorCss(styles?.['stroke-color']['checkbox-unchecked'].base)};
      }
      .checkbox__border {
        fill: ${colorCss(styles?.['fill-color']['checkbox-checked'].base)};
        stroke: ${colorCss(styles?.['stroke-color']['checkbox-checked'].base)};
      }
      .checkbox__check {
        stroke: ${colorCss(styles?.['stroke-color']['checkmark'].base)};
      }
    }

    /* Because focused and disabled are props, we can't use native CSS selectors */
    &:hover${isNotDisabled} {
      box-shadow:
        0 0 0 3px transparent,
        ${Elevation.Mid};
    }
    &${isFocused}${isNotDisabled} {
      svg {
        .checkbox__border--empty {
          fill: ${colorCss(styles?.['fill-color']['checkbox-unchecked'].focused)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-unchecked'].focused)};
        }
        .checkbox__border {
          fill: ${colorCss(styles?.['fill-color']['checkbox-checked'].focused)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-checked'].focused)};
        }
      }
    }
    &:active${isNotDisabled} {
      svg {
        .checkbox__border--empty {
          fill: ${colorCss(styles?.['fill-color']['checkbox-unchecked'].active)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-unchecked'].active)};
        }
        .checkbox__border {
          fill: ${colorCss(styles?.['fill-color']['checkbox-checked'].active)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-checked'].active)};
        }
      }
    }
    &${isDisabled} {
      cursor: not-allowed;

      svg {
        .checkbox__border--empty {
          fill: ${colorCss(styles?.['fill-color']['checkbox-unchecked'].disabled)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-unchecked'].disabled)};
        }
        .checkbox__border {
          fill: ${colorCss(styles?.['fill-color']['checkbox-checked'].disabled)};
          stroke: ${colorCss(styles?.['stroke-color']['checkbox-checked'].disabled)};
        }
      }
    }
  `}
`;

export type CheckboxProps = {
  id: string;
  className?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  hasError?: boolean;
  onChange: (checked: boolean) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  value: boolean | null | undefined | '';
  autoFocus?: boolean;
  styles?: InputCheckboxComponent['styles'];
  labelText: string;
  descriptionText?: string;
  'data-test'?: string;
};

const CheckboxBase = ({
  className,
  disabled,
  indeterminate,
  hasError,
  onChange,
  onFocus,
  onBlur,
  value,
  id,
  autoFocus,
  styles,
  labelText,
  descriptionText,
  'data-test': dataTest,
}: CheckboxProps) => {
  const [focused, { enable: setFocus, disable: clearFocus }] = useToggle();

  const handleChange = useCallback(() => {
    onChange(!value);
    inputRef.current?.focus();
  }, [onChange, value]);

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (inputRef.current) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  let describedBy: string | undefined;

  if (hasError) {
    describedBy = `${id}--error`;
  } else if (descriptionText) {
    describedBy = `${id}-description`;
  }

  return (
    <CheckboxContainer
      id={id}
      className={className}
      onClick={disabled ? undefined : handleChange}
      data-test={dataTest}
      aria-checked={!!value}
      aria-readonly={disabled}
      styles={styles}
    >
      <HiddenInput
        aria-label={labelText}
        aria-describedby={describedBy}
        aria-invalid={hasError}
        aria-errormessage={`${id}--error`}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={inputRef}
        data-test-toggle-status={value ? 'on' : 'off'}
        disabled={disabled}
        type='checkbox'
        checked={!!value}
        readOnly={true}
        onChange={() => null}
        autoFocus={autoFocus}
        onFocus={(e) => {
          setFocus();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          clearFocus();
          onBlur?.(e);
        }}
      />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <IconContainer focused={focused} disabled={disabled} styles={styles}>
        {indeterminate ? (
          <IconCheckboxIndeterminate />
        ) : value ? (
          <IconCheckboxChecked />
        ) : (
          <IconCheckboxUnchecked />
        )}
      </IconContainer>
      <Stack spacing={Spacing[0]}>
        <CheckboxLabel
          wrapper='label'
          wrapperProps={{ htmlFor: id }}
          styles={styles}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={disabled}
        >
          {labelText}
        </CheckboxLabel>
        {descriptionText && (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <CheckboxDescription id={`${id}-description`} styles={styles} disabled={disabled}>
            {descriptionText}
          </CheckboxDescription>
        )}
      </Stack>
    </CheckboxContainer>
  );
};

export default CheckboxBase;
